import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditBadge } from '../components/CreditBadge';
import { DocManiaLogoImage } from '../components/DocManiaLogo';
import { ProposalStepIndicator } from '../components/proposals/ProposalStepIndicator';
import {
  RERUN_TEMPLATE_FALLBACK_MESSAGE,
  applyRerunTemplateToSearchParams,
  resolveRerunTemplate,
} from '../lib/rerunTemplateFallback';
import { toast } from 'sonner';
import { scrollToResultCard, formatResultCardMeta } from '../lib/documentGenerationUi';
import {
  ArrowLeft, ArrowRight, Loader2, CheckCircle, Download, Plus, Trash2,
  Building2, FileText, Sparkles, ImageIcon, X, Target, Handshake,
  Coins, ClipboardList, Users, ShieldAlert,
} from 'lucide-react';
import { CurrencySelector } from '../components/ui/CurrencySelector';
import { getCurrencyDisplayPrefix } from '../utils/currencies';
import './BusinessPlanWizardPage.css';

/* ─── Constants ────────────────────────────────────────────────────────── */
const PROPOSAL_TYPE_DEFAULT = 'NGO Grant Proposal';
const DRAFT_VERSION = 'v1';
const AUTOSAVE_MS = 30000;
const TEMPLATES_PATH = '/documents/business-proposal/ngo-grant-proposal/templates';

const STEPS = [
  { id: 1, title: 'Organisation', icon: Building2, hint: 'Who you are and who you are applying to' },
  { id: 2, title: 'Programme', icon: FileText, hint: 'What you are requesting funding for' },
  { id: 3, title: 'Need & Approach', icon: Target, hint: 'Background, need & project description' },
  { id: 4, title: 'Objectives & Timeline', icon: ClipboardList, hint: 'Objectives and implementation phases' },
  { id: 5, title: 'Budget', icon: Coins, hint: 'How the grant will be used' },
  { id: 6, title: 'M&E & Sustainability', icon: CheckCircle, hint: 'Tracking progress and lasting impact' },
  { id: 7, title: 'Partners & Staff', icon: Users, hint: 'Partners and management team' },
  { id: 8, title: 'Risk & Generate', icon: ShieldAlert, hint: 'Risk, closing narratives & generate' },
];

const WIZARD_STEP_COUNT = STEPS.length;

const FOCUS_AREA_OPTIONS = [
  'Education',
  'Health',
  'Livelihoods',
  'Gender Equality',
  'Environment / Climate',
  'Human Rights',
  'Youth Development',
  'Agriculture / Food Security',
  'Wash / Water',
  'Other',
];

const PHASE_FIELDS = [
  { key: 'phase1', timeframeKey: 'phase1_timeframe', label: 'Planning & Preparation', testId: 'field-phase1' },
  { key: 'phase2', timeframeKey: 'phase2_timeframe', label: 'Implementation', testId: 'field-phase2' },
  { key: 'phase3', timeframeKey: 'phase3_timeframe', label: 'Monitoring', testId: 'field-phase3' },
  { key: 'phase4', timeframeKey: 'phase4_timeframe', label: 'Close-Out & Reporting', testId: 'field-phase4' },
];

const BLANK_OBJECTIVE = { objective: '', objective_desc: '' };
const BLANK_BUDGET = { item: '', amount: '', narrative: '' };
const BLANK_INDICATOR = { indicator: '', target: '' };
const BLANK_SUSTAINABILITY = { strategy: '', timeframe: '', responsible_party: '' };
const BLANK_PARTNER = { partner_organization: '', role: '' };
const BLANK_TEAM = { name: '', role: '', responsibility: '' };
const RISK_LEVELS = ['Low', 'Medium', 'High'];
const BLANK_RISK = { risk_name: '', risk_level: 'Medium', risk_mitigation: '' };

const normalizeGrantRisk = (r = {}) => ({
  risk_name: (r.risk_name || r.risk_factor || r.name || '').trim(),
  risk_level: r.risk_level || r.risk_likelihood || 'Medium',
  risk_mitigation: r.risk_mitigation || r.mitigation || '',
});
const emptyGrantRisk = () => ({ ...BLANK_RISK });

function completedRiskRows(risks) {
  return (Array.isArray(risks) ? risks : [])
    .map(normalizeGrantRisk)
    .filter((r) => r.risk_name && r.risk_mitigation);
}

const INITIAL_DATA = {
  // Step 1 — Organisation
  business_name: '',
  registration_status: '',
  year_founded: '',
  mission_statement: '',
  focus_area: '',
  business_address: '',
  business_email: '',
  business_phone: '',
  business_website: '',
  grant_body: '',
  // Step 2 — Programme
  project_title: '',
  funding_amount: '',
  project_duration: '',
  target_beneficiaries: '',
  currency: 'NGN',
  proposal_type: PROPOSAL_TYPE_DEFAULT,
  // Step 3 — Need & Approach
  org_background: '',
  statement_of_need: '',
  project_description: '',
  // Step 4 — Objectives & Timeline (phases as newline text in UI)
  objectives: [{ ...BLANK_OBJECTIVE }],
  phase1: '',
  phase2: '',
  phase3: '',
  phase4: '',
  phase1_timeframe: '',
  phase2_timeframe: '',
  phase3_timeframe: '',
  phase4_timeframe: '',
  // Step 5 — Budget
  budget: [{ ...BLANK_BUDGET }],
  // Step 6 — M&E & Sustainability
  monitoring_evaluation: '',
  evaluation_approach: '',
  indicators: [{ ...BLANK_INDICATOR }],
  sustainability: [{ ...BLANK_SUSTAINABILITY }],
  // Step 7 — Partners & Staff
  partners: [{ ...BLANK_PARTNER }],
  partnership_summary: '',
  mgt_team: [{ ...BLANK_TEAM }],
  // Step 8 — Risk & Generate
  risks: [emptyGrantRisk()],
  closing_statement: '',
  executive_summary: '',
};

const linesToPhaseItems = (text) =>
  String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((item) => ({ item }));

const phaseItemsToLines = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((row) => (typeof row === 'string' ? row : row?.item || ''))
      .map((s) => String(s).trim())
      .filter(Boolean)
      .join('\n');
  }
  return typeof value === 'string' ? value : '';
};

const normalizeList = (arr, blank, min = 1) => {
  if (Array.isArray(arr) && arr.length >= min) return arr.map((row) => ({ ...blank, ...row }));
  return Array.from({ length: min }, () => ({ ...blank }));
};

const normalizeIncomingData = (incoming = {}) => {
  const clean = { ...incoming };
  PHASE_FIELDS.forEach(({ key }) => {
    if (key in clean) clean[key] = phaseItemsToLines(clean[key]);
  });
  clean.objectives = normalizeList(clean.objectives, BLANK_OBJECTIVE, 1);
  clean.budget = normalizeList(clean.budget, BLANK_BUDGET, 1);
  clean.indicators = normalizeList(clean.indicators, BLANK_INDICATOR, 1);
  clean.sustainability = normalizeList(clean.sustainability, BLANK_SUSTAINABILITY, 1);
  clean.partners = normalizeList(clean.partners, BLANK_PARTNER, 1);
  clean.mgt_team = normalizeList(clean.mgt_team, BLANK_TEAM, 1);
  clean.risks = normalizeList(
    clean.risks?.length ? clean.risks.map(normalizeGrantRisk) : [{ ...BLANK_RISK }],
    BLANK_RISK,
    1,
  );
  if (!clean.proposal_type) clean.proposal_type = PROPOSAL_TYPE_DEFAULT;
  return clean;
};

const formatMoney = (n, currency) => {
  const num = Number(String(n ?? '').replaceAll(',', '')) || 0;
  const prefix = getCurrencyDisplayPrefix(currency);
  return `${prefix}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* ─── Shared UI helpers ────────────────────────────────────────────────── */
function RepeatableRow({ children, onRemove, removable }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-border bg-card/50 p-3">
      <div className="grid flex-1 gap-2">{children}</div>
      {removable && (
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="text-muted-foreground hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function FieldRow({ label, hint, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ImagePicker({ label, file, setFile, hint, testId }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);

  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image too large — keep under 5 MB.');
      return;
    }
    if (!f.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    setFile(f);
  };

  return (
    <FieldRow label={label} hint={hint}>
      {preview ? (
        <div className="flex items-start gap-3">
          <div className="relative inline-block">
            <img src={preview} alt={label} className="h-32 w-32 rounded-lg border object-cover" />
            <button type="button" onClick={() => setFile(null)} className="absolute -top-2 -right-2 rounded-full bg-destructive p-1 text-white shadow" data-testid={`${testId}-remove`}>
              <X className="h-3 w-3" />
            </button>
          </div>
          <label className="flex h-32 flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 px-3 text-center hover:bg-muted">
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
            <span className="mt-1 text-xs text-muted-foreground">Replace</span>
            <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handle} className="hidden" data-testid={testId} />
          </label>
        </div>
      ) : (
        <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 hover:bg-muted">
          <ImageIcon className="h-6 w-6 text-muted-foreground" />
          <span className="mt-1 text-xs text-muted-foreground">Upload</span>
          <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testId} />
        </label>
      )}
    </FieldRow>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-md border bg-card/50 px-3 py-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground truncate ml-2 max-w-[60%] text-right">{value}</span>
    </div>
  );
}

function AiActionButton({ label, loading, onClick, testId, icon: Icon = Sparkles }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick} disabled={loading} data-testid={testId} className="shrink-0">
      {loading
        ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Generating…</>
        : <><Icon className="mr-1 h-3 w-3" /> {label}</>}
    </Button>
  );
}

function AiSectionField({
  label,
  fieldKey,
  value,
  onChange,
  onSuggest,
  loading,
  rows = 5,
  hint,
  required,
  optional,
}) {
  return (
    <div className="space-y-2 rounded-lg border bg-card/50 p-3" data-testid={`section-${fieldKey}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
          {optional && <span className="text-xs font-normal text-muted-foreground"> (optional)</span>}
        </Label>
        <AiActionButton
          label="Get Suggestions"
          loading={loading}
          onClick={onSuggest}
          testId={`ai-suggest-${fieldKey}`}
        />
      </div>
      <Textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${fieldKey}`}
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/* ─── Main page ────────────────────────────────────────────────────────── */
export default function NgoGrantProposalWizardPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTemplateId = params.get('templateId');
  const [templateName, setTemplateName] = useState(
    () => params.get('templateName') || 'NGO Grant Proposal',
  );
  const rerunJobId = params.get('rerunJobId');
  const { user, api, refreshUser } = useAuth();

  const [templateId, setTemplateId] = useState(initialTemplateId);
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL_DATA);
  const [logoFile, setLogoFile] = useState(null);
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [processingStageMessage, setProcessingStageMessage] = useState('');
  const [jobResult, setJobResult] = useState(null);
  const [sectionAiLoading, setSectionAiLoading] = useState({});
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [credits, setCredits] = useState(20);
  const [showDraftPill, setShowDraftPill] = useState(false);

  const draftRestoredRef = useRef(false);
  const rerunRestoreRef = useRef(false);

  const draftKey = useMemo(
    () => (templateId && user?.id ? `ngo_grant_${DRAFT_VERSION}_${user.id}_${templateId}` : null),
    [templateId, user?.id],
  );

  const fileToDataURI = (file) => new Promise((resolve, reject) => {
    if (!file) { resolve(null); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const dataURIToFile = (dataURI, filename) => {
    if (!dataURI || typeof dataURI !== 'string') return null;
    try {
      const [header, base64] = dataURI.split(',');
      const mime = (header.match(/data:(.+?);/) || [])[1] || 'image/png';
      const bin = atob(base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      return new File([arr], filename, { type: mime });
    } catch {
      return null;
    }
  };

  const buildSubmitPayload = useCallback(() => ({
    ...data,
    proposal_type: data.proposal_type || PROPOSAL_TYPE_DEFAULT,
    phase1: linesToPhaseItems(data.phase1),
    phase2: linesToPhaseItems(data.phase2),
    phase3: linesToPhaseItems(data.phase3),
    phase4: linesToPhaseItems(data.phase4),
    risks: completedRiskRows(data.risks).map((r) => ({
      risk_name: r.risk_name,
      risk_level: r.risk_level,
      risk_mitigation: r.risk_mitigation,
    })),
    phase1_timeframe: data.phase1_timeframe,
    phase2_timeframe: data.phase2_timeframe,
    phase3_timeframe: data.phase3_timeframe,
    phase4_timeframe: data.phase4_timeframe,
  }), [data]);

  /* ── Redirect if no template ───────────────────────────────────────── */
  useEffect(() => {
    if (!templateId && !rerunJobId) {
      toast.error('No template selected. Pick a template first.');
      navigate(TEMPLATES_PATH);
    }
  }, [templateId, rerunJobId, navigate]);

  /* ── Credits ───────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/documents/types');
        const meta = (res.data?.types || []).find((t) => t.id === 'ngo_grant_proposal');
        if (meta?.credits) setCredits(meta.credits);
      } catch { /* default 20 */ }
    })();
  }, [api]);

  /* ── Rerun restore ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!rerunJobId || rerunRestoreRef.current) return;
    rerunRestoreRef.current = true;
    (async () => {
      try {
        const [rerunRes, logosRes] = await Promise.all([
          api.get(`/jobs/${rerunJobId}/rerun-data`),
          api.get(`/documents/${rerunJobId}/logos`).catch(() => ({ data: {} })),
        ]);
        const inputData = rerunRes.data?.input_data || {};
        const clean = Object.fromEntries(
          Object.entries(inputData).filter(([, v]) => v !== null && v !== undefined),
        );
        setData((d) => ({ ...d, ...normalizeIncomingData(clean) }));
        const { templateId: tid, templateName: tname, fallbackUsed } = resolveRerunTemplate(
          rerunRes.data,
        );
        if (tid) {
          setTemplateId(tid);
          if (tname) setTemplateName(tname);
          const current = new URLSearchParams(window.location.search);
          const needsUrlUpdate = current.get('templateId') !== tid
            || (tname && current.get('templateName') !== tname);
          if (needsUrlUpdate) {
            const sp = applyRerunTemplateToSearchParams(current, {
              templateId: tid,
              templateName: tname,
            });
            navigate(`${window.location.pathname}?${sp.toString()}`, { replace: true });
          }
        }
        if (fallbackUsed) {
          toast.info(RERUN_TEMPLATE_FALLBACK_MESSAGE);
        }
        const lf = dataURIToFile(logosRes.data?.logo, 'restored-logo.png');
        const pf = dataURIToFile(logosRes.data?.project_image, 'restored-project.png');
        if (lf) setLogoFile(lf);
        if (pf) setProjectImageFile(pf);
        toast.success('Previous proposal data restored.');
      } catch {
        toast.error('Could not load previous proposal — starting fresh.');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunJobId]);

  /* ── Draft restore ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!draftKey || rerunJobId) return;
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.data) {
        setData((d) => ({ ...d, ...normalizeIncomingData(parsed.data) }));
      }
      if (parsed?.step) setStep(parsed.step);
      const lf = dataURIToFile(parsed?.images?.logo, 'draft-logo.png');
      const pf = dataURIToFile(parsed?.images?.project_image, 'draft-project.png');
      if (lf) setLogoFile(lf);
      if (pf) setProjectImageFile(pf);
      setShowDraftPill(true);
      toast.success('Draft restored — pick up where you left off.');
    } catch {
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    }
  }, [draftKey, rerunJobId]);

  /* ── Autosave ──────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!draftKey || rerunJobId || jobResult) return;
    const id = setInterval(async () => {
      try {
        let logoUri = null;
        let projUri = null;
        if (logoFile && logoFile.size <= 1024 * 1024) logoUri = await fileToDataURI(logoFile);
        if (projectImageFile && projectImageFile.size <= 1024 * 1024) projUri = await fileToDataURI(projectImageFile);
        const payload = JSON.stringify({
          data, step,
          images: { logo: logoUri, project_image: projUri },
          saved_at: new Date().toISOString(),
        });
        if (payload.length > 4 * 1024 * 1024) {
          localStorage.setItem(draftKey, JSON.stringify({ data, step, images: {}, saved_at: new Date().toISOString() }));
        } else {
          localStorage.setItem(draftKey, payload);
        }
      } catch { /* quota */ }
    }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [draftKey, rerunJobId, data, step, logoFile, projectImageFile, jobResult]);

  const clearDraft = () => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setShowDraftPill(false);
    toast.info('Draft cleared.');
  };
  const dismissDraftPill = () => setShowDraftPill(false);

  const setField = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const setRowField = (listKey, idx, key, val) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].map((row, i) => (i === idx ? { ...row, [key]: val } : row)) }));
  const addRow = (listKey, blank) =>
    setData((d) => ({ ...d, [listKey]: [...d[listKey], { ...blank }] }));
  const removeRow = (listKey, idx, min = 1) =>
    setData((d) => ({ ...d, [listKey]: d[listKey].length <= min ? d[listKey] : d[listKey].filter((_, i) => i !== idx) }));

  /* ── Validation ────────────────────────────────────────────────────── */
  const validate = (s) => {
    if (s === 1) {
      if (!data.business_name.trim()) return 'Organisation name is required.';
      if (!data.grant_body.trim()) return 'Grant body / funder is required.';
    }
    if (s === 2) {
      if (!data.project_title.trim()) return 'Project title is required.';
      if (!String(data.funding_amount).trim()) return 'Funding amount is required.';
    }
    if (s === 4) {
      const objectives = (data.objectives || []).filter((r) => (r.objective || '').trim());
      if (!objectives.length) return 'Add at least one objective.';
    }
    if (s === 5) {
      const budget = (data.budget || []).filter((r) => (r.item || '').trim());
      if (!budget.length) return 'Add at least one budget line item.';
    }
    if (s === 7) {
      const team = (data.mgt_team || []).filter((r) => (r.role || '').trim());
      if (!team.length) return 'Add at least one management team role.';
    }
    return null;
  };

  const goNext = () => {
    const err = validate(step);
    if (err) { toast.error(err); return; }
    setStep((n) => Math.min(WIZARD_STEP_COUNT, n + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => {
    setStep((n) => Math.max(1, n - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const draftSection = async (sectionKey) => {
    if (sectionAiLoading[sectionKey]) return;
    setSectionAiLoading((s) => ({ ...s, [sectionKey]: true }));
    try {
      const res = await api.post('/documents/ai-ngo-grant-section', {
        section: sectionKey,
        ...buildSubmitPayload(),
      });
      const text = res.data?.text || '';
      if (!text) throw new Error('Empty response');
      setField(sectionKey, text);
      toast.success('Draft ready — edit as needed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not draft this section.');
    } finally {
      setSectionAiLoading((s) => ({ ...s, [sectionKey]: false }));
    }
  };

  const draftAssist = async (assistType, loadingKey, onResult, extra = {}) => {
    if (sectionAiLoading[loadingKey]) return;
    setSectionAiLoading((s) => ({ ...s, [loadingKey]: true }));
    try {
      const res = await api.post('/documents/ai-ngo-grant-assist', {
        assist_type: assistType,
        ...buildSubmitPayload(),
        ...extra,
      });
      onResult(res.data || {});
      toast.success('Suggestion ready — edit as needed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate a suggestion.');
    } finally {
      setSectionAiLoading((s) => ({ ...s, [loadingKey]: false }));
    }
  };

  const handleBudgetSuggest = async () => {
    if (budgetLoading) return;
    if (!data.project_title.trim()) {
      toast.error('Enter the project title before generating budget suggestions.');
      return;
    }
    if (!String(data.funding_amount).trim()) {
      toast.error('Enter the funding amount before generating budget suggestions.');
      return;
    }
    setBudgetLoading(true);
    try {
      const res = await api.post('/documents/ai-ngo-grant-budget', buildSubmitPayload());
      const rows = res.data?.budget || [];
      if (!rows.length) {
        toast.error('No budget rows returned. Please retry.');
        return;
      }
      setData((d) => ({
        ...d,
        budget: normalizeList(
          rows.map((row) => ({
            item: row.item || '',
            amount: row.amount != null ? String(row.amount) : '',
            narrative: row.narrative || '',
          })),
          BLANK_BUDGET,
          1,
        ),
      }));
      toast.success('Budget draft ready — amounts are estimated allocations, not verified costs.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate budget suggestions.');
    } finally {
      setBudgetLoading(false);
    }
  };

  const handleSustainabilitySuggest = (idx) => {
    draftAssist('sustainability_strategy', `sustainability_${idx}`, (payload) => {
      const text = payload.text || '';
      if (text) setRowField('sustainability', idx, 'strategy', text);
    });
  };

  const handleIndicatorSuggest = (idx) => {
    draftAssist('indicator_pair', `indicator_${idx}`, (payload) => {
      if (payload.indicator) setRowField('indicators', idx, 'indicator', payload.indicator);
      if (payload.target) setRowField('indicators', idx, 'target', payload.target);
    });
  };

  const handleRiskMitigationSuggest = (idx) => {
    const risk = data.risks[idx];
    const riskName = (risk?.risk_name || '').trim();
    if (!riskName) {
      toast.error('Enter a Risk Factor before requesting a mitigation suggestion.');
      return;
    }
    draftAssist(
      'risk_mitigation',
      `risk_${idx}`,
      (payload) => {
        const text = payload.risk_mitigation || '';
        if (text) setRowField('risks', idx, 'risk_mitigation', text);
      },
      {
        risk_name: riskName,
        risk_level: risk?.risk_level || 'Medium',
      },
    );
  };

  /* ── Poll job ──────────────────────────────────────────────────────── */
  const pollJob = async (jobId) => {
    setPolling(true);
    setProcessingStageMessage('');
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        if (res.data.processing_stage_message) {
          setProcessingStageMessage(res.data.processing_stage_message);
        }
        if (res.data.status === 'preview_ready' || res.data.status === 'completed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          setProcessingStageMessage('');
          refreshUser?.();
          if (draftKey) {
            try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
            setShowDraftPill(false);
          }
          toast.success('Your NGO grant proposal is ready.');
          scrollToResultCard();
          return;
        }
        if (res.data.status === 'failed') {
          setJobResult(res.data);
          setPolling(false);
          setGenerating(false);
          setProcessingStageMessage('');
          toast.error(res.data.error_message || 'Generation failed.');
          return;
        }
      } catch { /* keep polling */ }
    }
    setPolling(false);
    setGenerating(false);
    setProcessingStageMessage('');
    toast.error('Generation timed out. Please try again.');
  };

  const handleGenerate = async () => {
    if (generating) return;
    const err = validate(8) || validate(1) || validate(2) || validate(4) || validate(5) || validate(7);
    if (err) { toast.error(err); return; }
    if (!templateId) {
      toast.error('No template selected.');
      return;
    }
    setGenerating(true);
    setJobResult(null);
    try {
      const fd = new FormData();
      fd.append('service_type', 'document_generation');
      fd.append('document_type', 'ngo_grant_proposal');
      fd.append('template_id', templateId);
      fd.append('input_data', JSON.stringify(buildSubmitPayload()));
      if (logoFile) fd.append('logo', logoFile);
      if (projectImageFile) fd.append('project_image', projectImageFile);

      const res = await api.post('/documents/ngo-grant-proposal', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Generation started — this may take 20–30 seconds.');
      pollJob(res.data.job_id);
    } catch (err) {
      setGenerating(false);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Generation failed.';
      if (status === 402) {
        toast.error('Insufficient credits.');
        navigate('/credits');
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDownload = async () => {
    if (!jobResult) return;
    let currentJob = jobResult;
    if (currentJob.status === 'preview_ready') {
      try {
        const fin = await api.post(`/documents/${currentJob.id}/finalize-download`);
        if (fin.data?.balance != null) {
          currentJob = { ...currentJob, status: 'completed' };
          setJobResult(currentJob);
        }
      } catch (err) {
        if (err.response?.status === 402) {
          toast.error('Not enough credits.');
          navigate('/credits');
        } else {
          toast.error(err.response?.data?.detail || 'Could not unlock document.');
        }
        return;
      }
    }
    try {
      const res = await api.get(`/storage/download/${currentJob.id}`, { responseType: 'blob' });
      const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '';
      const headerMatch = cd.match(/filename="?([^";\n]+)"?/i);
      const downloadName = headerMatch?.[1]
        || currentJob.metadata?.filename
        || `${data.business_name || PROPOSAL_TYPE_DEFAULT} NGO Grant Proposal.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed.');
    }
  };

  /* ── Step renderers ──────────────────────────────────────────────────── */
  const renderStep1 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <FieldRow label="Organisation Name" required>
        <Input value={data.business_name} onChange={(e) => setField('business_name', e.target.value)} data-testid="field-business_name" placeholder="Hope Community Foundation" />
      </FieldRow>
      <FieldRow label="Grant Body / Funder" required hint="Name of the funding source on the cover.">
        <Input value={data.grant_body} onChange={(e) => setField('grant_body', e.target.value)} data-testid="field-grant_body" placeholder="Ford Foundation" />
      </FieldRow>
      <FieldRow label="Registration Status">
        <Input value={data.registration_status} onChange={(e) => setField('registration_status', e.target.value)} data-testid="field-registration_status" placeholder="Registered NGO / Incorporated Trustee" />
      </FieldRow>
      <FieldRow label="Year Founded">
        <Input value={data.year_founded} onChange={(e) => setField('year_founded', e.target.value)} data-testid="field-year_founded" placeholder="2014" />
      </FieldRow>
      <div className="md:col-span-2">
        <FieldRow label="Mission Statement" hint="One or two sentences describing your mission.">
          <Textarea rows={3} value={data.mission_statement} onChange={(e) => setField('mission_statement', e.target.value)} data-testid="field-mission_statement" />
        </FieldRow>
      </div>
      <FieldRow label="Focus Area">
        <Select value={data.focus_area} onValueChange={(v) => setField('focus_area', v)}>
          <SelectTrigger data-testid="field-focus_area"><SelectValue placeholder="Select focus area" /></SelectTrigger>
          <SelectContent>
            {FOCUS_AREA_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </FieldRow>
      <FieldRow label="Organisation Address">
        <Input value={data.business_address} onChange={(e) => setField('business_address', e.target.value)} data-testid="field-business_address" />
      </FieldRow>
      <FieldRow label="Email">
        <Input type="email" value={data.business_email} onChange={(e) => setField('business_email', e.target.value)} data-testid="field-business_email" />
      </FieldRow>
      <FieldRow label="Phone">
        <Input value={data.business_phone} onChange={(e) => setField('business_phone', e.target.value)} data-testid="field-business_phone" />
      </FieldRow>
      <FieldRow label="Website">
        <Input value={data.business_website} onChange={(e) => setField('business_website', e.target.value)} data-testid="field-business_website" />
      </FieldRow>
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
        <ImagePicker label="Organisation Logo (optional)" file={logoFile} setFile={setLogoFile} hint="PNG or JPG — cover page logo." testId="upload-logo" />
        <ImagePicker label="Project Image (optional)" file={projectImageFile} setFile={setProjectImageFile} hint="PNG or JPG — image related to this programme." testId="upload-project-image" />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="md:col-span-2">
        <FieldRow label="Project Title" required>
          <Input value={data.project_title} onChange={(e) => setField('project_title', e.target.value)} data-testid="field-project_title" placeholder="Girls STEM Scholarship Programme" />
        </FieldRow>
      </div>
      <FieldRow label="Funding Amount Requested" required>
        <Input value={data.funding_amount} onChange={(e) => setField('funding_amount', e.target.value)} data-testid="field-funding_amount" placeholder="50000" />
      </FieldRow>
      <FieldRow label="Currency">
        <CurrencySelector value={data.currency} onChange={(v) => setField('currency', v)} />
      </FieldRow>
      <FieldRow label="Project Duration">
        <Input value={data.project_duration} onChange={(e) => setField('project_duration', e.target.value)} data-testid="field-project_duration" placeholder="18 months" />
      </FieldRow>
      <FieldRow label="Proposal Type" hint="Shown on the cover / header of the proposal.">
        <Input value={data.proposal_type} onChange={(e) => setField('proposal_type', e.target.value)} data-testid="field-proposal_type" placeholder={PROPOSAL_TYPE_DEFAULT} />
      </FieldRow>
      <div className="md:col-span-2">
        <FieldRow label="Target Beneficiaries">
          <Textarea rows={3} value={data.target_beneficiaries} onChange={(e) => setField('target_beneficiaries', e.target.value)} data-testid="field-target_beneficiaries" placeholder="e.g. 200 adolescent girls in rural Kaduna" />
        </FieldRow>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <AiSectionField
        label="Organisation Background"
        fieldKey="org_background"
        value={data.org_background}
        onChange={(v) => setField('org_background', v)}
        onSuggest={() => draftSection('org_background')}
        loading={sectionAiLoading.org_background}
        hint="Draft notes — Get Suggestions can polish into a full narrative."
        rows={5}
      />
      <AiSectionField
        label="Statement of Need"
        fieldKey="statement_of_need"
        value={data.statement_of_need}
        onChange={(v) => setField('statement_of_need', v)}
        onSuggest={() => draftSection('statement_of_need')}
        loading={sectionAiLoading.statement_of_need}
        hint="Describe the community need this grant will address."
        rows={5}
      />
      <AiSectionField
        label="Project Description"
        fieldKey="project_description"
        value={data.project_description}
        onChange={(v) => setField('project_description', v)}
        onSuggest={() => draftSection('project_description')}
        loading={sectionAiLoading.project_description}
        hint="Activities, approach, and how beneficiaries are reached."
        rows={5}
      />
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="text-sm font-medium">
          Project Objectives <span className="text-destructive">*</span>
        </div>
        {data.objectives.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.objectives.length > 1} onRemove={() => removeRow('objectives', idx, 1)}>
            <div className="grid gap-3 md:grid-cols-2">
              <FieldRow label="Objective" hint="One short sentence — put detail in Description.">
                <Input value={row.objective} onChange={(e) => setRowField('objectives', idx, 'objective', e.target.value)} data-testid={`field-objectives-${idx}-objective`} placeholder="e.g. Train 150 beneficiaries in marketplace skills" />
              </FieldRow>
              <FieldRow label="Description">
                <Textarea rows={2} value={row.objective_desc} onChange={(e) => setRowField('objectives', idx, 'objective_desc', e.target.value)} data-testid={`field-objectives-${idx}-objective_desc`} placeholder="How this objective will be achieved" />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('objectives', BLANK_OBJECTIVE)} data-testid="add-objective-btn">
          <Plus className="mr-1 h-3 w-3" /> Add objective
        </Button>
      </div>

      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Enter one activity or milestone per line for each phase, plus an optional timeframe.</p>
        {PHASE_FIELDS.map(({ key, timeframeKey, label, testId }) => (
          <div key={key} className="space-y-2 rounded-lg border bg-muted/20 p-3">
            <FieldRow label={label} hint="One item per line.">
              <Input
                value={data[timeframeKey]}
                onChange={(e) => setField(timeframeKey, e.target.value)}
                data-testid={`${testId}-timeframe`}
                placeholder="e.g. Months 1-2"
              />
            </FieldRow>
            <Textarea
              rows={4}
              value={data[key]}
              onChange={(e) => setField(key, e.target.value)}
              data-testid={testId}
              placeholder={'e.g. Recruit project staff\nFinalize partner MoUs'}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          Budget Line Items <span className="text-destructive">*</span>
        </div>
        <AiActionButton
          label="Get Suggestions"
          loading={budgetLoading}
          onClick={handleBudgetSuggest}
          testId="ai-suggest-budget"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Optional draft — Get Suggestions proposes estimated proportional allocations, not verified costs.
      </p>
      {data.budget.map((row, idx) => (
        <RepeatableRow key={idx} removable={data.budget.length > 1} onRemove={() => removeRow('budget', idx, 1)}>
          <div className="grid gap-3 md:grid-cols-3">
            <FieldRow label="Item">
              <Input value={row.item} onChange={(e) => setRowField('budget', idx, 'item', e.target.value)} data-testid={`field-budget-${idx}-item`} placeholder="e.g. Scholarships" />
            </FieldRow>
            <FieldRow label="Amount">
              <Input value={row.amount} onChange={(e) => setRowField('budget', idx, 'amount', e.target.value)} data-testid={`field-budget-${idx}-amount`} placeholder="25000" />
            </FieldRow>
            <FieldRow label="Budget Narrative">
              <Textarea rows={2} value={row.narrative} onChange={(e) => setRowField('budget', idx, 'narrative', e.target.value)} data-testid={`field-budget-${idx}-narrative`} placeholder="Why this line is needed" />
            </FieldRow>
          </div>
        </RepeatableRow>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => addRow('budget', BLANK_BUDGET)} data-testid="add-budget-btn">
        <Plus className="mr-1 h-3 w-3" /> Add budget item
      </Button>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-4">
      <AiSectionField
        label="Monitoring & Evaluation"
        fieldKey="monitoring_evaluation"
        value={data.monitoring_evaluation}
        onChange={(v) => setField('monitoring_evaluation', v)}
        onSuggest={() => draftSection('monitoring_evaluation')}
        loading={sectionAiLoading.monitoring_evaluation}
        rows={4}
      />
      <AiSectionField
        label="Evaluation Approach"
        fieldKey="evaluation_approach"
        value={data.evaluation_approach}
        onChange={(v) => setField('evaluation_approach', v)}
        onSuggest={() => draftSection('evaluation_approach')}
        loading={sectionAiLoading.evaluation_approach}
        rows={4}
      />

      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="text-sm font-medium">Indicators</div>
        {data.indicators.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.indicators.length > 1} onRemove={() => removeRow('indicators', idx, 1)}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="text-xs font-medium text-muted-foreground">Indicator {idx + 1}</div>
              <AiActionButton
                label="Get Suggestions"
                loading={sectionAiLoading[`indicator_${idx}`]}
                onClick={() => handleIndicatorSuggest(idx)}
                testId={`ai-suggest-indicator-${idx}`}
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <FieldRow label="Indicator">
                <Input value={row.indicator} onChange={(e) => setRowField('indicators', idx, 'indicator', e.target.value)} data-testid={`field-indicators-${idx}-indicator`} />
              </FieldRow>
              <FieldRow label="Target">
                <Input value={row.target} onChange={(e) => setRowField('indicators', idx, 'target', e.target.value)} data-testid={`field-indicators-${idx}-target`} />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('indicators', BLANK_INDICATOR)} data-testid="add-indicator-btn">
          <Plus className="mr-1 h-3 w-3" /> Add indicator
        </Button>
      </div>

      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="text-sm font-medium">Sustainability Strategies</div>
        {data.sustainability.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.sustainability.length > 1} onRemove={() => removeRow('sustainability', idx, 1)}>
            <div className="grid gap-3 md:grid-cols-3">
              <FieldRow label="Strategy">
                <div className="flex flex-col gap-2">
                  <Input value={row.strategy} onChange={(e) => setRowField('sustainability', idx, 'strategy', e.target.value)} data-testid={`field-sustainability-${idx}-strategy`} />
                  <AiActionButton
                    label="Get Suggestions"
                    loading={sectionAiLoading[`sustainability_${idx}`]}
                    onClick={() => handleSustainabilitySuggest(idx)}
                    testId={`ai-suggest-sustainability-${idx}`}
                  />
                </div>
              </FieldRow>
              <FieldRow label="Timeframe">
                <Input value={row.timeframe} onChange={(e) => setRowField('sustainability', idx, 'timeframe', e.target.value)} data-testid={`field-sustainability-${idx}-timeframe`} />
              </FieldRow>
              <FieldRow label="Responsible Party">
                <Input value={row.responsible_party} onChange={(e) => setRowField('sustainability', idx, 'responsible_party', e.target.value)} data-testid={`field-sustainability-${idx}-responsible_party`} />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('sustainability', BLANK_SUSTAINABILITY)} data-testid="add-sustainability-btn">
          <Plus className="mr-1 h-3 w-3" /> Add strategy
        </Button>
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="text-sm font-medium">Partners</div>
        {data.partners.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.partners.length > 1} onRemove={() => removeRow('partners', idx, 1)}>
            <div className="grid gap-3 md:grid-cols-2">
              <FieldRow label="Partner Organisation">
                <Input value={row.partner_organization} onChange={(e) => setRowField('partners', idx, 'partner_organization', e.target.value)} data-testid={`field-partners-${idx}-partner_organization`} />
              </FieldRow>
              <FieldRow label="Role">
                <Input value={row.role} onChange={(e) => setRowField('partners', idx, 'role', e.target.value)} data-testid={`field-partners-${idx}-role`} />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('partners', BLANK_PARTNER)} data-testid="add-partner-btn">
          <Plus className="mr-1 h-3 w-3" /> Add partner
        </Button>
      </div>

      <AiSectionField
        label="Partnership Summary"
        fieldKey="partnership_summary"
        value={data.partnership_summary}
        onChange={(v) => setField('partnership_summary', v)}
        onSuggest={() => draftSection('partnership_summary')}
        loading={sectionAiLoading.partnership_summary}
        hint="Optional draft — Get Suggestions can polish this."
        rows={4}
      />

      <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
        <div className="text-sm font-medium">
          Management Team <span className="text-destructive">*</span>
        </div>
        {data.mgt_team.map((row, idx) => (
          <RepeatableRow key={idx} removable={data.mgt_team.length > 1} onRemove={() => removeRow('mgt_team', idx, 1)}>
            <div className="grid gap-3 md:grid-cols-3">
              <FieldRow label="Name">
                <Input value={row.name} onChange={(e) => setRowField('mgt_team', idx, 'name', e.target.value)} data-testid={`field-mgt_team-${idx}-name`} placeholder="e.g. Jane Okonkwo" />
              </FieldRow>
              <FieldRow label="Role">
                <Input value={row.role} onChange={(e) => setRowField('mgt_team', idx, 'role', e.target.value)} data-testid={`field-mgt_team-${idx}-role`} />
              </FieldRow>
              <FieldRow label="Responsibility">
                <Input value={row.responsibility} onChange={(e) => setRowField('mgt_team', idx, 'responsibility', e.target.value)} data-testid={`field-mgt_team-${idx}-responsibility`} />
              </FieldRow>
            </div>
          </RepeatableRow>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => addRow('mgt_team', BLANK_TEAM)} data-testid="add-mgt-team-btn">
          <Plus className="mr-1 h-3 w-3" /> Add team member
        </Button>
      </div>
    </div>
  );

  const renderStep8 = () => {
    const objectiveCount = (data.objectives || []).filter((r) => (r.objective || '').trim()).length;
    const budgetCount = (data.budget || []).filter((r) => (r.item || '').trim()).length;
    const riskCount = completedRiskRows(data.risks).length;

    return (
      <div className="space-y-4">
        <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
          <div className="text-sm font-medium">
            Risk & Mitigation
            <span className="ml-2 text-xs font-normal text-muted-foreground">(optional)</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Add programme risks with Risk Factor, Risk Level, and Mitigation Strategy.
            If left blank, the Risk Management section is omitted from the document.
          </p>
          {data.risks.map((risk, idx) => (
            <RepeatableRow key={idx} removable={data.risks.length > 1} onRemove={() => removeRow('risks', idx, 1)}>
              <div className="grid gap-3 md:grid-cols-2">
                <FieldRow label="Risk Factor">
                  <Input
                    value={risk.risk_name ?? ''}
                    onChange={(e) => setRowField('risks', idx, 'risk_name', e.target.value)}
                    placeholder="e.g. Security concerns in target communities"
                    data-testid={`field-risk-${idx}-name`}
                  />
                </FieldRow>
                <FieldRow label="Risk Level">
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={risk.risk_level ?? 'Medium'}
                    onChange={(e) => setRowField('risks', idx, 'risk_level', e.target.value)}
                    data-testid={`field-risk-${idx}-level`}
                  >
                    {RISK_LEVELS.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </FieldRow>
              </div>
              <FieldRow label="Mitigation Strategy">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <Textarea
                    rows={3}
                    className="flex-1"
                    value={risk.risk_mitigation ?? ''}
                    onChange={(e) => setRowField('risks', idx, 'risk_mitigation', e.target.value)}
                    placeholder="How will you reduce or manage this risk?"
                    data-testid={`field-risk-${idx}-mitigation`}
                  />
                  <AiActionButton
                    label="Get Suggestions"
                    loading={sectionAiLoading[`risk_${idx}`]}
                    onClick={() => handleRiskMitigationSuggest(idx)}
                    testId={`ai-suggest-risk-${idx}`}
                  />
                </div>
              </FieldRow>
            </RepeatableRow>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => addRow('risks', BLANK_RISK)} data-testid="add-risk-btn">
            <Plus className="mr-1 h-3 w-3" /> Add risk
          </Button>
        </div>
        <AiSectionField
          label="Closing Statement"
          fieldKey="closing_statement"
          value={data.closing_statement}
          onChange={(v) => setField('closing_statement', v)}
          onSuggest={() => draftSection('closing_statement')}
          loading={sectionAiLoading.closing_statement}
          rows={5}
        />
        <AiSectionField
          label="Executive Summary"
          fieldKey="executive_summary"
          value={data.executive_summary}
          onChange={(v) => setField('executive_summary', v)}
          onSuggest={() => draftSection('executive_summary')}
          loading={sectionAiLoading.executive_summary}
          rows={6}
        />

        <Card data-testid="review-summary-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Review before generating</CardTitle>
            <CardDescription>
              {credits} credits are deducted when you generate.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            <SummaryRow label="Organisation" value={data.business_name || '—'} />
            <SummaryRow label="Grant body" value={data.grant_body || '—'} />
            <SummaryRow label="Project" value={data.project_title || '—'} />
            <SummaryRow label="Funding" value={formatMoney(data.funding_amount, data.currency)} />
            <SummaryRow label="Objectives" value={`${objectiveCount}`} />
            <SummaryRow label="Budget rows" value={`${budgetCount}`} />
            <SummaryRow label="Risks" value={riskCount ? `${riskCount}` : '—'} />
            <SummaryRow label="Logo" value={logoFile ? '✓' : '—'} />
            <SummaryRow label="Project image" value={projectImageFile ? '✓' : '—'} />
            <SummaryRow label="Template" value={templateName} />
          </CardContent>
        </Card>

        {jobResult && (jobResult.status === 'preview_ready' || jobResult.status === 'completed') ? (
          <Card className="border-primary/30 bg-primary/5" data-testid="result-card">
            <CardContent className="py-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-primary" />
                  <div>
                    <p className="font-semibold">Your NGO grant proposal is ready</p>
                    <p className="text-sm text-muted-foreground">
                      {formatResultCardMeta(jobResult, credits) || `${credits} credits deducted when generated.`}
                    </p>
                  </div>
                </div>
                <Button onClick={handleDownload} data-testid="download-btn" className="glow-button">
                  <Download className="mr-2 h-4 w-4" />
                  {jobResult.status === 'preview_ready' ? `Unlock & Download — ${credits} credits` : 'Download PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center justify-end gap-3">
              <Button onClick={handleGenerate} disabled={generating || polling} className="glow-button px-8" data-testid="generate-btn">
                {generating
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{polling ? (processingStageMessage || 'Building…') : 'Submitting…'}</>
                  : <><FileText className="mr-2 h-4 w-4" /> Generate Proposal</>}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const stepRenderers = {
    1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4,
    5: renderStep5, 6: renderStep6, 7: renderStep7, 8: renderStep8,
  };

  const currentStep = STEPS[step - 1];
  const Icon = currentStep.icon;

  if (!templateId && !rerunJobId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="ngo-grant-wizard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16" data-testid="ngo-grant-proposal-wizard">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2" data-testid="back-to-dashboard">
            <DocManiaLogoImage size={28} />
          </button>
          <div className="flex items-center gap-3">
            <CreditBadge balance={user?.credit_balance || 0} />
            <Button variant="ghost" size="sm" onClick={() => navigate(TEMPLATES_PATH)}>
              <ArrowLeft className="mr-1 h-3 w-3" /> Templates
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate(TEMPLATES_PATH)} className="mb-2 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-1 h-3 w-3" /> Change template
            </button>
            <div className="bp-header-title">
              <h1 className="flex items-center gap-2 text-2xl font-semibold sm:text-3xl">
                <Handshake className="h-6 w-6 text-primary" /> NGO Grant Proposal Builder
              </h1>
              <p>Complete all steps to generate a funder-ready NGO grant proposal</p>
            </div>
            <p className="text-sm text-muted-foreground">{currentStep.hint} · Step {step} of {WIZARD_STEP_COUNT}</p>
          </div>
          <Badge variant="secondary" className="hidden sm:inline-flex">
            {credits} credits on generate
          </Badge>
        </div>

        <ProposalStepIndicator steps={STEPS} step={step} />

        {showDraftPill && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm" data-testid="draft-restored-pill">
            <div className="flex items-center gap-2 text-emerald-900">
              <CheckCircle className="h-4 w-4" />
              <span><strong>Draft restored</strong> — auto-saved every 30 seconds.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={dismissDraftPill} data-testid="dismiss-draft-pill">
                Dismiss
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearDraft} data-testid="clear-draft-btn" className="text-destructive hover:text-destructive">
                Reset draft
              </Button>
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Icon className="h-5 w-5 text-primary" /> {currentStep.title}
            </CardTitle>
          </CardHeader>
          <CardContent>{stepRenderers[step]()}</CardContent>
        </Card>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={goBack} disabled={step === 1 || generating} data-testid="wizard-back-btn">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div className="text-xs text-muted-foreground">{step}/{WIZARD_STEP_COUNT}</div>
          {step < WIZARD_STEP_COUNT ? (
            <Button type="button" onClick={goNext} disabled={generating} data-testid="wizard-next-btn" className="glow-button">
              Next <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <span />
          )}
        </div>
      </main>
    </div>
  );
}
